import { Auth } from "aws-amplify";
import Cookies from "universal-cookie";

import { getConfigs } from "app/utils/configs";
import { localDevEnabled } from "app/utils/localDev";

export interface CookieOptions {
  domain: string;
  path: string;
  secure: boolean;
  sameSite: "strict" | "lax" | "none";
}

export function getAuthCookieOptions(): CookieOptions {
  return {
    domain:
      window.location.hostname.split(".").length > 2
        ? window.location.hostname.replace(/^[^.]*/, "")
        : window.location.hostname,
    path: "/",
    secure: window.location.protocol === "https:",
    // Lax still lets a top-level SSO navigation into a sibling service carry the
    // cookie, while blocking it from being sent on cross-site subrequests.
    sameSite: "lax",
  };
}

async function getSession() {
  return await Auth.currentSession().catch(() => {
    Auth.signOut();
  });
}

// Local-development backends (platform's `just local-console`, see
// app/utils/localDev) skip the Cognito sign-in but still want a real JWT so the
// cross-service validation path runs.  The local sign-in form (LocalIdpLogin)
// records which seeded user to act as; we mint that user's token straight from
// the local IdP (via the vite /api/local-token proxy in local-dev mode) and reuse it.
const LOCAL_IDP_USER_KEY = "stacklet-local-idp-user";

let localToken: { user: string; promise: Promise<string> } | null = null;

export function getLocalIdpUser(): string | null {
  return localStorage.getItem(LOCAL_IDP_USER_KEY);
}

export function setLocalIdpUser(username: string): void {
  localStorage.setItem(LOCAL_IDP_USER_KEY, username);
  localToken = null; // a different user needs a different token
}

export function clearLocalIdpUser(): void {
  localStorage.removeItem(LOCAL_IDP_USER_KEY);
  localToken = null;
  document.cookie = "stacklet-auth=; path=/; max-age=0";
  document.cookie = "stacklet-access=; path=/; max-age=0";
  document.cookie = "stacklet-refresh=; path=/; max-age=0";
}

/**
 * Sets the cross-service JWT cookies used for SSO redirects out of the
 * console: Redash reads `stacklet-auth`, Superset reads `stacklet-access`.
 * Both services run on localhost so a host-only cookie reaches them
 * regardless of port; SameSite=Lax so a top-level navigation from the
 * console carries it. The local IdP mints one token per user, so both
 * cookies get the same value here — in production (see authCookieHandler)
 * they hold the ID token and access token respectively.
 */
export function setLocalIdpCookies(token: string): void {
  document.cookie = `stacklet-auth=${token}; path=/; SameSite=Lax`;
  document.cookie = `stacklet-access=${token}; path=/; SameSite=Lax`;
}

async function getLocalToken(): Promise<string> {
  const user = getLocalIdpUser();
  if (!user) {
    // The sign-in gate renders children only once a user is chosen, so this
    // is unreachable in practice; fail soft rather than throw mid-request.
    return "Unauthenticated";
  }
  if (!localToken || localToken.user !== user) {
    const { deployment } = await getConfigs();
    const tokenUrl = deployment.local_idp_token_url;
    if (!tokenUrl) {
      throw new Error("local IdP token URL missing from deployment config");
    }
    const promise = fetch(`${tokenUrl}?username=${encodeURIComponent(user)}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`local IdP token request failed: ${res.status}`);
        }
        return res.json();
      })
      .then((body) => {
        const token = body.access_token as string;
        setLocalIdpCookies(token);
        return token;
      })
      .catch((err) => {
        // Don't cache a failure -- let the next request retry.
        localToken = null;
        throw err;
      });
    localToken = { user, promise };
  }
  return localToken.promise;
}

// The session type Auth.currentSession() resolves to, without importing
// amazon-cognito-identity-js directly (it's a transitive dependency).
type CognitoSession = Awaited<ReturnType<typeof Auth.currentSession>>;

/**
 * Whether the user is signed in, without `getSession`'s sign-out side effect.
 *
 * Route loaders run before the sign-in gate has decided anything, so they ask
 * this before starting a query: an unauthenticated request comes back a 401,
 * and the page would then be holding a query reference that signing in cannot
 * repair.
 */
export async function hasAuthSession(): Promise<boolean> {
  if (localDevEnabled()) {
    return getLocalIdpUser() !== null;
  }

  return Auth.currentSession().then(
    () => true,
    () => false,
  );
}

// The token getters accept an already-resolved session so a caller holding one
// (e.g. authCookieHandler) can read every token from a single currentSession()
// call instead of re-fetching per token. Called without one, they resolve it
// themselves.
export async function getAuthToken(session?: CognitoSession): Promise<string> {
  if (localDevEnabled()) {
    return getLocalToken();
  }

  const resolved = session ?? (await getSession());

  if (resolved) {
    return resolved.getAccessToken().getJwtToken();
  }

  return "Unauthenticated";
}

export async function getIdToken(session?: CognitoSession): Promise<string> {
  const resolved = session ?? (await getSession());

  if (resolved) {
    return resolved.getIdToken().getJwtToken();
  }

  return "Unauthenticated";
}

// FUTURE (Amplify v6): `Auth.currentSession()` and the `getRefreshToken()`
// accessor both go away in v6 — `fetchAuthSession()` deliberately omits the
// refresh token, since the team intends it to be used only by Amplify
// internally (see maintainer rationale:
// https://github.com/aws-amplify/amplify-js/issues/14324). Their suggested
// alternative — pass the access token and let Amplify auto-refresh — does not
// work for us: the whole point of this cookie is to let Sinistral bootstrap its
// own session from the refresh token, which Amplify's internal refresh can't do
// for it. So on migration we'll need to keep obtaining the raw refresh token,
// either by reading the `CognitoIdentityServiceProvider.{clientId}.{username}
// .refreshToken` storage key (accepting its instability, mitigated with a test
// that fails if the key shape changes) or by implementing a custom
// TokenProvider.
export async function getRefreshToken(
  session?: CognitoSession,
): Promise<string> {
  const resolved = session ?? (await getSession());

  if (resolved) {
    return resolved.getRefreshToken().getToken();
  }

  return "Unauthenticated";
}

export async function authCookieHandler() {
  const cookies = new Cookies();
  for (const key in cookies.getAll()) {
    if (
      key.startsWith("CognitoIdentityServiceProvider.") &&
      key.endsWith(".userData") &&
      Object.keys(cookies.get(key)).length === 0
    ) {
      // Work around https://github.com/aws-amplify/amplify-js/issues/11106
      // For some reason, if the userData cookie is empty, the Auth.currentSession
      // below explodes and the .catch() on it doesn't help. Removing the cookie
      // just forces Amplify to rebuild it.
      cookies.remove(key, getAuthCookieOptions());
    }
  }
  // can't use getSession because that will cause a signOut event infinite loop
  const session = await Auth.currentSession().catch(() => null);

  if (session) {
    // We deliberately mirror only the three Cognito tokens into cookies (which
    // are always browser-readable regardless of storage backend). Keeping the
    // token set self-contained leaves us free to move the rest of Amplify's
    // session state back into localStorage.
    //
    // Pass the resolved session into each getter so all three tokens come from
    // this one currentSession() call — re-fetching per token would risk a
    // getter seeing an expired/absent session and storing "Unauthenticated".
    cookies.set(
      "stacklet-auth",
      await getIdToken(session),
      getAuthCookieOptions(),
    );
    cookies.set(
      "stacklet-access",
      await getAuthToken(session),
      getAuthCookieOptions(),
    );
    cookies.set(
      "stacklet-refresh",
      await getRefreshToken(session),
      getAuthCookieOptions(),
    );
  } else {
    cookies.remove("stacklet-auth", getAuthCookieOptions());
    cookies.remove("stacklet-access", getAuthCookieOptions());
    cookies.remove("stacklet-refresh", getAuthCookieOptions());
  }
}
